/**
 * SidebarNav — renders the main navigation list.
 */

import React from 'react'

import { cn } from '@elsapiens/utils'
import { NavLink, useLocation } from 'react-router-dom'

import { type NavItem } from './nav-items'

const isMac = typeof navigator !== 'undefined' && /mac/i.test(navigator.platform)
const altSymbol = isMac ? '⌥' : 'Alt+'

interface SidebarNavProps {
  items: NavItem[]
  onItemClick?: () => void
}

function isItemActive(item: NavItem, pathname: string): boolean {
  if (item.exact) {
    return pathname === item.path
  }
  if (pathname === item.path || pathname.startsWith(`${item.path  }/`)) {
    return true
  }
  return (item.matchPaths ?? []).some(p => pathname === p || pathname.startsWith(`${p  }/`))
}

export function SidebarNav({ items, onItemClick }: SidebarNavProps): React.JSX.Element {
  const { pathname } = useLocation()

  return (
    <nav className="flex-1 overflow-x-hidden overflow-y-auto">
      {items.map((item) => (
        <div key={item.id} className="el-px-xs">
          <NavLink
            className={cn(
              'el-nav-item',
              isItemActive(item, pathname) && 'el-nav-item-active',
            )}
            to={item.path}
            onClick={onItemClick}
          >
            {item.icon && <span className="el-nav-icon">{item.icon}</span>}
            <span className="flex-1">{item.label}</span>
            {item.shortcutKey && (
              <kbd className="hidden lg:inline-flex items-center text-[10px] font-mono text-muted-foreground bg-muted border border-border rounded px-1 py-0.5 leading-none select-none">
                {altSymbol}{item.shortcutKey}
              </kbd>
            )}
          </NavLink>
        </div>
      ))}
    </nav>
  )
}
